import type { ButtonHTMLAttributes } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'

export type TableActionMenuItem = {
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  hasDividerAbove?: boolean
}

interface TableActionMenuProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  ariaLabel: string
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
  items: TableActionMenuItem[]
  menuClassName?: string
}

export function TableActionMenu({
  ariaLabel,
  isOpen,
  onToggle,
  onClose,
  items,
  className = '',
  menuClassName = '',
  type = 'button',
  ...props
}: TableActionMenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [coords, setCoords] = useState<{
    top: number
    left: number
    placeAbove: boolean
  } | null>(null)

  const updatePosition = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const estimatedHeight = Math.min(items.length * 40 + 20, 320)
    const menuWidth = 240
    const spaceBelow = window.innerHeight - rect.bottom
    const placeAbove = spaceBelow < estimatedHeight + 10 && rect.top > estimatedHeight

    const top = placeAbove ? rect.top - 6 : rect.bottom + 6
    const left = Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12))

    setCoords({
      top,
      left,
      placeAbove,
    })
  }

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    updatePosition()
    const handleScrollOrResize = () => updatePosition()
    window.addEventListener('resize', handleScrollOrResize)
    window.addEventListener('scroll', handleScrollOrResize, true)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      window.removeEventListener('resize', handleScrollOrResize)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [isOpen, onClose])

  return (
    <div className="relative inline-block text-left">
      <button
        ref={triggerRef}
        type={type}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={onToggle}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-[6px] bg-transparent text-[#64748b] transition hover:bg-[#f1f5f9] hover:text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#64748b]/20 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
        {...props}
      >
        <MoreVertical className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
      </button>

      {isOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: coords?.placeAbove ? undefined : coords?.top,
                bottom: coords?.placeAbove && coords ? window.innerHeight - coords.top : undefined,
                left: coords?.left ?? 0,
                zIndex: 9999,
                borderWidth: '0.67px',
                borderStyle: 'solid',
                borderColor: '#E5E7EB',
              }}
              className={`flex w-60 max-h-[380px] flex-col overflow-y-auto rounded-[12px] bg-[#FFFFFF] p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.12)] ${menuClassName}`}
            >
              <div className="flex w-full flex-col">
                {items.map((item) => {
                  const Icon = item.icon

                  return (
                    <div key={item.label} className="w-full">
                      {item.hasDividerAbove && (
                        <div className="my-1 border-t border-[#f1f5f9]" style={{ borderWidth: '0.67px' }} />
                      )}
                      <button
                        type="button"
                        disabled={item.disabled}
                        onClick={() => {
                          if (item.disabled) return
                          item.onClick()
                          onClose()
                        }}
                        className={`flex w-full flex-row items-center gap-3 rounded-[6px] px-3 py-2 text-left text-sm font-semibold transition hover:bg-[#f8fafc] disabled:cursor-not-allowed disabled:opacity-45 ${
                          item.danger
                            ? 'text-[#ef4444] hover:bg-[#fef2f2]'
                            : 'text-[#1e293b]'
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 shrink-0 stroke-[1.75] ${item.danger ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                          aria-hidden="true"
                        />
                        <span className="whitespace-normal leading-snug">{item.label}</span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
